import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Skeleton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
  Divider,
  alpha,
  useTheme,
} from '@mui/material';
import { ArrowLeft, Clock3, Send } from 'lucide-react';
import { broadcastApi } from '../../../api';
import { templatesApi } from '../../../api/templates.api';
import { getMediaCategory, uploadApi } from '../../../api/upload.api';
import MediaUpload from '../../../components/common/MediaUpload';
import useUIStore from '../../../store/useUIStore';

const BroadcastCreatePage = ({
  templates = [],
  userAttributes = [{}],
  broadcastNumbers = [],
  sess = { role: 'user', uid: '', mobile: '' },
}) => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const cardSx = {
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    boxShadow: 'none',
  };
  const primaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: `0 10px 20px ${alpha(theme.palette.primary.main, 0.2)}`,
  };
  const secondaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    borderColor: alpha(theme.palette.primary.main, 0.25),
    '&:hover': {
      borderColor: alpha(theme.palette.primary.main, 0.45),
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
  };
  const [broadcastName, setBroadcastName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [message, setMessage] = useState('');
  const [approvedTemplates, setApprovedTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [recipientType, setRecipientType] = useState('all');
  const [selectedTags, setSelectedTags] = useState([]);
  const [customRecipients, setCustomRecipients] = useState('');
  const [scheduleType, setScheduleType] = useState('now');
  const [scheduledDateTime, setScheduledDateTime] = useState('');
  const [selectedBroadcastNumber, setSelectedBroadcastNumber] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const showSnackbar = useUIStore((state) => state.showSnackbar);
  const [selectedTemplateDetails, setSelectedTemplateDetails] = useState(null);
  const [loadingTemplateDetails, setLoadingTemplateDetails] = useState(false);

  // Template variables state
  const [headerVariables, setHeaderVariables] = useState({});
  const [bodyVariables, setBodyVariables] = useState({});
  const [headerMediaHandle, setHeaderMediaHandle] = useState('');

  const isAdmin = sess.role === 'admin' || sess.role === 'superadmin';

  const getErrorMessage = (error, fallback) =>
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallback;

  useEffect(() => {
    const rescheduleId = searchParams.get('reschedule');
    if (rescheduleId) {
      fetchBroadcastForReschedule(rescheduleId);
    }
  }, [searchParams]);

  useEffect(() => {
    loadApprovedTemplates();
  }, []);

  useEffect(() => {
    if (status.type !== 'success' || !status.message) return;
    showSnackbar({ message: status.message, severity: 'success' });
    setStatus({ type: '', message: '' });
  }, [showSnackbar, status]);

  useEffect(() => {
    let isMounted = true;
    if (!selectedTemplate) {
      setSelectedTemplateDetails(null);
      setLoadingTemplateDetails(false);
      return undefined;
    }
    setLoadingTemplateDetails(true);
    templatesApi.getById(selectedTemplate)
      .then((response) => {
        if (!isMounted) return;
        const details = response?.data?.data || response?.data || null;
        setSelectedTemplateDetails(details);
      })
      .catch(() => {
        if (isMounted) setSelectedTemplateDetails(null);
      })
      .finally(() => {
        if (isMounted) setLoadingTemplateDetails(false);
      });
    return () => {
      isMounted = false;
    };
  }, [selectedTemplate]);

  const loadApprovedTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await templatesApi.getApproved({ page: 1, limit: 100 });
      const list =
        response?.data?.data ||
        response?.data?.templates ||
        response?.data?.results ||
        response?.data ||
        [];
      setApprovedTemplates(Array.isArray(list) ? list : []);
    } catch (error) {
      setApprovedTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchBroadcastForReschedule = async (id) => {
    try {
      const response = await broadcastApi.getById(id);
      const details = response?.data?.data || response?.data || {};
      setBroadcastName(details.name || '');
      setSelectedTemplate(details.templateId || details.template || '');
      setMessage(details.message || '');
      setRecipientType(details.recipientType || 'all');
      setSelectedTags(details.tags || []);
      setSelectedBroadcastNumber(details.broadcastNumber || details.broadcastNumberId || '');
    } catch (error) {
      console.error('Error fetching broadcast:', error);
      const message = getErrorMessage(error, 'Unable to load broadcast for reschedule');
      setStatus({ type: 'error', message });
      showSnackbar({ message, severity: 'error' });
    }
  };

  const getTagOptions = () => {
    const options = [];
    if (userAttributes[0]) {
      for (const key in userAttributes[0]) {
        if (
          Object.prototype.hasOwnProperty.call(userAttributes[0], key) &&
          typeof userAttributes[0][key] === 'string' &&
          userAttributes[0][key]
        ) {
          const values = userAttributes[0][key].split(',');
          values.forEach((value) => {
            if (value.trim()) options.push(value.trim());
          });
        }
      }
    }
    return [...new Set(options)];
  };

  const templateOptions = templates.length > 0 ? templates : approvedTemplates;
  const getTemplateValue = (template) =>
    template?.id || template?.t_id || template?.templateId || '';
  const getTemplateChannel = (template) => {
    const channel = template?.channel || template?.t_channel || template?.platform;
    return channel ? `${channel}`.toLowerCase() : 'whatsapp';
  };

  // Extract variables like {{1}}, {{2}} from text
  const extractVariables = (text) => {
    if (!text) return [];
    const regex = /\{\{(\d+)\}\}/g;
    const matches = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      matches.push(match[1]);
    }
    return [...new Set(matches)].sort((a, b) => Number(a) - Number(b));
  };

  const getTemplateHeader = (template) => {
    if (!template) return null;
    const headerComponent = selectedTemplateDetails?.components?.find(
      (comp) => String(comp?.type || '').toLowerCase() === 'header'
    );
    if (headerComponent) {
      const format = headerComponent.format || headerComponent.content?.format || '';
      const text = headerComponent.content?.text || '';
      return { type: format ? `${format}`.toLowerCase() : '', text };
    }
    const headerType =
      template.header?.type ||
      template.header_type ||
      template.t_htype ||
      template.headerType ||
      template.headerFormat ||
      template.header_format ||
      template.t_header_type ||
      '';
    const headerText =
      template.header?.text ||
      template.header_text ||
      template.t_htext ||
      template.headerText ||
      template.header_content ||
      template.t_header_text ||
      '';
    return { type: headerType ? `${headerType}`.toLowerCase() : '', text: headerText };
  };

  // Get template body text
  const getTemplateBody = (template) => {
    const bodyComponent = selectedTemplateDetails?.components?.find(
      (comp) => String(comp?.type || '').toLowerCase() === 'body'
    );
    if (bodyComponent) {
      return bodyComponent.content?.text || '';
    }
    const bodyValue = template?.body;
    if (typeof bodyValue === 'string') return bodyValue;
    return (
      bodyValue?.text ||
      template?.content ||
      template?.t_btext ||
      template?.message ||
      template?.bodyText ||
      template?.body_text ||
      template?.t_body ||
      ''
    );
  };

  // Get selected template data
  const selectedTemplateData = useMemo(() => {
    if (!selectedTemplate) return null;
    return templateOptions.find(
      (t) => `${getTemplateValue(t)}` === `${selectedTemplate}`
    );
  }, [selectedTemplate, templateOptions]);

  // Extract header and body variables from selected template
  const templateHeaderInfo = useMemo(() => {
    return getTemplateHeader(selectedTemplateData);
  }, [selectedTemplateData]);

  const templateHeaderVariables = useMemo(() => {
    if (!templateHeaderInfo?.text) return [];
    return extractVariables(templateHeaderInfo.text);
  }, [templateHeaderInfo]);

  const templateBodyVariables = useMemo(() => {
    const bodyText = getTemplateBody(selectedTemplateData);
    return extractVariables(bodyText);
  }, [selectedTemplateData]);

  // Check if template has media header (image, video, document)
  const templateHasMediaHeader = useMemo(() => {
    if (!templateHeaderInfo?.type) return false;
    const mediaTypes = ['image', 'video', 'document'];
    return mediaTypes.includes(templateHeaderInfo.type.toLowerCase());
  }, [templateHeaderInfo]);

  // Get media category for MediaUpload component
  const headerMediaCategory = useMemo(() => {
    if (!templateHeaderInfo?.type) return 'all';
    const type = templateHeaderInfo.type.toLowerCase();
    if (type === 'image') return 'image';
    if (type === 'video') return 'video';
    if (type === 'document') return 'document';
    return 'all';
  }, [templateHeaderInfo]);

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplate(templateId);
    setSelectedTemplateDetails(null);

    // Reset variables when template changes
    setHeaderVariables({});
    setBodyVariables({});
    setHeaderMediaHandle('');

    const template = templateOptions.find(
      (t) => `${getTemplateValue(t)}` === `${templateId}`
    );
    if (template) {
      setMessage(
        template.body ||
        template.content ||
        template.t_btext ||
        template.message ||
        ''
      );
    }
  };

  // Handle header variable input change
  const handleHeaderVariableChange = (varIndex, value) => {
    setHeaderVariables((prev) => ({
      ...prev,
      [varIndex]: value,
    }));
  };

  // Handle body variable input change
  const handleBodyVariableChange = (varIndex, value) => {
    setBodyVariables((prev) => ({
      ...prev,
      [varIndex]: value,
    }));
  };

  // Handle media upload complete
  const handleMediaUploadComplete = (result) => {
    setHeaderMediaHandle(result.handle);
  };

  const handleMediaChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMediaFile(file);

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setMediaPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setMediaPreview(null);
      }
    }
  };

  // Build components array for payload
  const buildComponentsPayload = () => {
    const components = [];

    // Build header component only when header has text variables
    if (templateHeaderVariables.length > 0) {
      // Text header with variables
      const headerParams = templateHeaderVariables.map((varIndex) => ({
        type: 'text',
        text: headerVariables[varIndex] || '',
      }));
      if (headerParams.length > 0) {
        components.push({
          type: 'header',
          parameters: headerParams,
        });
      }
    }

    // Build body component (required by backend, even with zero params)
    const bodyParams = templateBodyVariables.map((varIndex) => ({
      type: 'text',
      text: bodyVariables[varIndex] || '',
    }));
    components.push({
      type: 'body',
      parameters: bodyParams,
    });

    return components;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!broadcastName.trim()) {
      showSnackbar({ message: 'Please enter broadcast name', severity: 'error' });
      return;
    }

    if (!message.trim()) {
      showSnackbar({ message: 'Please enter message or select a template', severity: 'error' });
      return;
    }

    if (scheduleType === 'scheduled' && !scheduledDateTime) {
      showSnackbar({ message: 'Please select date and time for scheduled broadcast', severity: 'error' });
      return;
    }

    if (!selectedBroadcastNumber) {
      showSnackbar({ message: 'Please select a broadcast number', severity: 'error' });
      return;
    }

    // Validate template variables if template is selected
    if (selectedTemplate) {
      // Check header variables
      for (const varIndex of templateHeaderVariables) {
        if (!headerVariables[varIndex]?.trim()) {
          showSnackbar({ message: `Please fill in header variable {{${varIndex}}}`, severity: 'error' });
          return;
        }
      }

      // Check body variables
      for (const varIndex of templateBodyVariables) {
        if (!bodyVariables[varIndex]?.trim()) {
          showSnackbar({ message: `Please fill in body variable {{${varIndex}}}`, severity: 'error' });
          return;
        }
      }

      // Check media header
      if (templateHasMediaHeader && !headerMediaHandle) {
        showSnackbar({ message: `Please upload ${headerMediaCategory} for template header`, severity: 'error' });
        return;
      }
      if (!templateHasMediaHeader && headerMediaHandle) {
        showSnackbar({ message: 'Media is not allowed for this template', severity: 'error' });
        return;
      }
    }

    setSubmitting(true);
    setStatus({ type: '', message: '' });

    const sendType = selectedBroadcastNumber ? 'group' : (recipientType === 'tags' ? 'tag' : 'group');
    let mediaId = '';
    let mediaType = '';

    // Handle additional media file upload (only when no template is selected)
    if (!selectedTemplate && mediaFile) {
      try {
        const uploadResponse = await uploadApi.uploadMedia(mediaFile);
        const uploadData = uploadResponse?.data || {};
        mediaId = uploadData.handle || uploadData.media_id || uploadData.id || '';
        mediaType = getMediaCategory(mediaFile.type) || '';
      } catch (uploadError) {
        const uploadMessage =
          uploadError?.response?.data?.error || uploadError?.message || 'Failed to upload media';
        setStatus({ type: 'error', message: uploadMessage });
        showSnackbar({ message: uploadMessage, severity: 'error' });
        setSubmitting(false);
        return;
      }
    }

    // Build components payload for template variables
    const components = buildComponentsPayload();

    // Determine the final media id and type
    const finalMediaId = templateHasMediaHeader ? (headerMediaHandle || null) : (!selectedTemplate ? (mediaId || null) : null);
    const finalMediaType = templateHasMediaHeader ? headerMediaCategory : (!selectedTemplate ? (mediaType || null) : null);

    const payload = {
      send_type: sendType,
      bgid: sendType === 'group' ? Number(selectedBroadcastNumber) : undefined,
      tag_id: sendType === 'tag' ? selectedTags : undefined,
      template_id: selectedTemplate ? Number(selectedTemplate) : null,
      channel: getTemplateChannel(selectedTemplateData),
      has_media: Boolean(finalMediaId),
      media_type: finalMediaType,
      media_id: finalMediaId,
      payload: components.length > 0 ? { components } : {},
      user_id: sess.uid,
    };

    try {
      const response = await broadcastApi.create(payload);
      const responseData = response?.data || {};
      if (responseData?.success === false || responseData?.error) {
        const message = getErrorMessage({ response: { data: responseData } }, 'Failed to create broadcast');
        setStatus({ type: 'error', message });
        showSnackbar({ message, severity: 'error' });
        return;
      }
      const created = response?.data?.data || response?.data || {};
      const createdId = created.id || created.broadcastId;

      if (scheduleType === 'scheduled' && createdId) {
        const scheduleResponse = await broadcastApi.schedule({ campaignId: createdId, scheduledAt: scheduledDateTime });
        const scheduleData = scheduleResponse?.data || {};
        if (scheduleData?.success === false || scheduleData?.error) {
          const message = getErrorMessage({ response: { data: scheduleData } }, 'Failed to schedule broadcast');
          setStatus({ type: 'error', message });
          showSnackbar({ message, severity: 'error' });
          return;
        }
      } else if (createdId) {
        const sendResponse = await broadcastApi.send({ campaignId: createdId });
        const sendData = sendResponse?.data || {};
        if (sendData?.success === false || sendData?.error) {
          const message = getErrorMessage({ response: { data: sendData } }, 'Failed to send broadcast');
          setStatus({ type: 'error', message });
          showSnackbar({ message, severity: 'error' });
          return;
        }
      }

      setStatus({
        type: 'success',
        message: scheduleType === 'now' ? 'Broadcast sent successfully!' : 'Broadcast scheduled successfully!',
      });
      navigate('/broadcast/schedule');
    } catch (error) {
      console.error('Error creating broadcast:', error);
      const message = getErrorMessage(error, 'Failed to create broadcast');
      setStatus({ type: 'error', message });
      showSnackbar({ message, severity: 'error' });
    }
    finally {
      setSubmitting(false);
    }
  };

  const tagOptions = getTagOptions();

  return (
    <Box sx={{ p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.02), minHeight: '100%' }}>
      <Card sx={{ ...cardSx, mb: 2, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)` }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="h5" fontWeight={700}>Create Broadcast</Typography>
              <Typography variant="body2" color="text.secondary">
                Target recipients with approved templates
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<ArrowLeft size={16} />}
              onClick={() => navigate('/broadcast')}
            >
              Back
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {status.message && status.type === 'error' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {status.message}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Stack spacing={2}>
              <Card sx={cardSx}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Broadcast Details
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Broadcast Name"
                      value={broadcastName}
                      onChange={(e) => setBroadcastName(e.target.value)}
                      placeholder="Enter broadcast name"
                      required
                      fullWidth
                    />
                    <FormControl fullWidth>
                      <FormLabel>Select Template (Optional)</FormLabel>
                      {loadingTemplates && templateOptions.length === 0 ? (
                        <Skeleton variant="rounded" height={40} />
                      ) : (
                        <Select
                          value={selectedTemplate}
                          onChange={handleTemplateChange}
                          displayEmpty
                          size="small"
                        >
                          <MenuItem value="">
                            <em>-- Create Custom Message --</em>
                          </MenuItem>
                          {templateOptions.map((template, index) => {
                            const id = getTemplateValue(template);
                            const name = template.name || template.t_tname || template.title || 'Template';
                            return (
                              <MenuItem key={id || index} value={id}>
                                {name}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      )}
                    </FormControl>
                    <TextField
                      label="Message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Enter your broadcast message"
                      required
                      multiline
                      minRows={4}
                      helperText={`Characters: ${message.length}`}
                      fullWidth
                      InputProps={{
                        readOnly: !!selectedTemplate,
                      }}
                    />

                    {/* Template Variables Section */}
                    {selectedTemplate && loadingTemplateDetails && (
                      <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 2 }} />
                        <Skeleton variant="text" width={180} height={28} />
                        <Stack spacing={1.5} sx={{ mt: 1 }}>
                          <Skeleton variant="rounded" height={40} />
                          <Skeleton variant="rounded" height={40} />
                        </Stack>
                      </Box>
                    )}
                    {selectedTemplate && (templateHasMediaHeader || templateHeaderVariables.length > 0 || templateBodyVariables.length > 0) && (
                      <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                          Template Variables
                        </Typography>

                        {/* Header Media Upload */}
                        {templateHasMediaHeader && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight={600} gutterBottom>
                              Header {headerMediaCategory.charAt(0).toUpperCase() + headerMediaCategory.slice(1)} *
                            </Typography>
                            <MediaUpload
                              category={headerMediaCategory}
                              onUploadComplete={handleMediaUploadComplete}
                              onError={(err) => setStatus({ type: 'error', message: err })}
                              compact
                            />
                          </Box>
                        )}

                        {/* Header Text Variables */}
                        {templateHeaderVariables.length > 0 && !templateHasMediaHeader && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight={600} gutterBottom>
                              Header Variables
                            </Typography>
                            <Stack spacing={1.5}>
                              {templateHeaderVariables.map((varIndex) => (
                                <TextField
                                  key={`header-${varIndex}`}
                                  label={`Header Variable {{${varIndex}}}`}
                                  value={headerVariables[varIndex] || ''}
                                  onChange={(e) => handleHeaderVariableChange(varIndex, e.target.value)}
                                  placeholder={`Enter value for {{${varIndex}}}`}
                                  size="small"
                                  required
                                  fullWidth
                                />
                              ))}
                            </Stack>
                          </Box>
                        )}

                        {/* Body Variables */}
                        {templateBodyVariables.length > 0 && (
                          <Box>
                            <Typography variant="body2" fontWeight={600} gutterBottom>
                              Body Variables
                            </Typography>
                            <Stack spacing={1.5}>
                              {templateBodyVariables.map((varIndex) => (
                                <TextField
                                  key={`body-${varIndex}`}
                                  label={`Body Variable {{${varIndex}}}`}
                                  value={bodyVariables[varIndex] || ''}
                                  onChange={(e) => handleBodyVariableChange(varIndex, e.target.value)}
                                  placeholder={`Enter value for {{${varIndex}}}`}
                                  size="small"
                                  required
                                  fullWidth
                                />
                              ))}
                            </Stack>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Additional Media (only when no template is selected) */}
                    {!selectedTemplate && (
                      <Stack spacing={1}>
                        <Typography variant="body2" fontWeight={600}>
                          Media (Optional)
                        </Typography>
                        <Button variant="outlined" component="label" size="small">
                          Choose File
                          <input
                            type="file"
                            hidden
                            accept="image/*,video/*,application/pdf"
                            onChange={handleMediaChange}
                          />
                        </Button>
                        {mediaPreview && (
                          <Box
                            component="img"
                            src={mediaPreview}
                            alt="Preview"
                            sx={{ maxWidth: 320, maxHeight: 320, borderRadius: 1, mt: 1 }}
                          />
                        )}
                      </Stack>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={cardSx}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Select Recipients
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      name="recipientType"
                      value={recipientType}
                      onChange={(e) => setRecipientType(e.target.value)}
                    >
                      <FormControlLabel value="all" control={<Radio />} label="All Contacts" />
                      <FormControlLabel value="tags" control={<Radio />} label="By Tags" />
                      <FormControlLabel value="custom" control={<Radio />} label="Custom Numbers" />
                    </RadioGroup>
                  </FormControl>

                  {recipientType === 'tags' && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
                      {tagOptions.map((tag) => {
                        const selected = selectedTags.includes(tag);
                        return (
                          <Chip
                            key={tag}
                            label={tag}
                            color={selected ? 'primary' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            onClick={() => {
                              if (selected) {
                                setSelectedTags(selectedTags.filter((t) => t !== tag));
                              } else {
                                setSelectedTags([...selectedTags, tag]);
                              }
                            }}
                            sx={{ mb: 1 }}
                          />
                        );
                      })}
                    </Stack>
                  )}

                  {recipientType === 'custom' && (
                    <TextField
                      label="Custom Numbers"
                      value={customRecipients}
                      onChange={(e) => setCustomRecipients(e.target.value)}
                      placeholder="Enter phone numbers separated by commas (e.g., 1234567890, 0987654321)"
                      multiline
                      minRows={3}
                      fullWidth
                      sx={{ mt: 2 }}
                    />
                  )}
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              <Card sx={cardSx}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Schedule
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      name="scheduleType"
                      value={scheduleType}
                      onChange={(e) => setScheduleType(e.target.value)}
                    >
                      <FormControlLabel value="now" control={<Radio />} label="Send Now" />
                      <FormControlLabel value="scheduled" control={<Radio />} label="Schedule for Later" />
                    </RadioGroup>
                  </FormControl>

                  {scheduleType === 'scheduled' && (
                    <TextField
                      label="Date & Time"
                      type="datetime-local"
                      value={scheduledDateTime}
                      onChange={(e) => setScheduledDateTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: new Date().toISOString().slice(0, 16) }}
                      required
                      fullWidth
                    />
                  )}
                </CardContent>
              </Card>

              <Card sx={cardSx}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Broadcast Number *
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedBroadcastNumber}
                      onChange={(e) => setSelectedBroadcastNumber(e.target.value)}
                      displayEmpty
                      required
                    >
                      <MenuItem value="">
                        <em>-- Select Number --</em>
                      </MenuItem>
                      {broadcastNumbers.map((number, index) => (
                        <MenuItem key={index} value={number.id}>
                          {number.name} ({number.number})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </CardContent>
              </Card>

              <Stack direction="row" spacing={1}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={submitting}
                  startIcon={scheduleType === 'now' ? <Send size={16} /> : <Clock3 size={16} />}
                  sx={primaryButtonSx}
                >
                  {submitting
                    ? 'Saving...'
                    : scheduleType === 'now'
                      ? 'Send Broadcast'
                      : 'Schedule Broadcast'}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/broadcast')}
                  disabled={submitting}
                  sx={secondaryButtonSx}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default BroadcastCreatePage;
